import React from 'react';
import {Linking, Platform, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import RewardsProgramImage from '../../components/rewards/ProgramImage';
import Button from '../../components/themed/Button';
import KeyboardAwareScrollView from '../../components/themed/KeyboardAwareScrollView';
import Text from '../../components/themed/Text';
import ContentView from '../../components/ui-controls/layout/ContentView';
import config from '../../config';
import {AppRoute} from '../../models/AppRoute';
import {RouteNavigatorScreenProps} from '../../models/navigationTypes';
import {logError} from '../../utils/errorLogging';

// DIS-1328: terminal screen shown when a non-authoritative lite sign-in returns
// HTTP 422 account_not_found — the phone has no Zoho/CRM contact, so we can't verify
// the member's details. Retrying the same number won't fix it (the contact is
// missing, not the code), so there is NO retry affordance; the user is directed to
// support. 422 deliberately sidesteps the global 401 logout interceptor — this is a
// business failure, not an auth failure.
const AccountNotFoundScreen = observer(
  ({navigation}: RouteNavigatorScreenProps<AppRoute.REWARDS_ACCOUNT_NOT_FOUND>) => {
    const handleContactSupport = async () => {
      const url = `mailto:${config.SUPPORT_EMAIL}`;
      // RNW Linking.openURL routes mailto through window.open(_blank), which
      // leaves a dead about:blank tab. Navigate the current window so the
      // browser hands off to the mail client (matches utils/navigation.ts).
      if (Platform.OS === 'web') {
        window.location.href = url;
        return;
      }
      try {
        await Linking.openURL(url);
      } catch (e) {
        logError(e, 'AccountNotFoundScreen.handleContactSupport');
      }
    };

    const handleMemberSignIn = () => {
      navigation.navigate(AppRoute.REWARDS_SIGN_IN);
    };

    return (
      <KeyboardAwareScrollView
        style={{flex: 1}}
        contentContainerStyle={{flex: 1}}
        keyboardShouldPersistTaps="always">
        <ContentView parentStyle={{flex: 1, paddingBottom: 16}} style={{flex: 1}}>
          <RewardsProgramImage variant="onLight" />

          <View>
            <Text variant="heading1">WE COULDN&apos;T FIND YOUR ACCOUNT</Text>
          </View>
          <Text>
            We couldn&apos;t verify your account details. Please contact Customer Support at{' '}
            <Text
              testID="account-not-found-support-email"
              accessibilityRole="link"
              accessibilityLabel={`Contact support at ${config.SUPPORT_EMAIL}`}
              style={{textDecorationLine: 'underline'}}
              onPress={handleContactSupport}>
              {config.SUPPORT_EMAIL}
            </Text>{' '}
            to get this resolved.
          </Text>

          <View style={{gap: 8, marginTop: 40}}>
            <Button title={'SIGN IN AS A MEMBER'} onPress={handleMemberSignIn} />
          </View>
        </ContentView>
      </KeyboardAwareScrollView>
    );
  },
);

export default AccountNotFoundScreen;
